import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coffee } from 'lucide-react';
import { useAuthStore } from '../../../store/authStore';
import { updateShopSettings, getMyShopPrivate } from '../../../services/shop-service';
import { getSquareCatalogItems, type CatalogStampItem } from '../../../services/square-service';
import ShopQRCode from '../ShopQRCode';
import { useShopSettingsLoad } from './useShopSettingsLoad';
import OwnerShell, { OwnerLoadingShell } from '../OwnerShell';
import CoralHeader from '../../dashboard/CoralHeader';

// Shared coral form styles.
const INPUT_CLS = 'w-full p-3 bg-background rounded-[8px] border-2 border-hairline focus:border-coral outline-none text-primary text-sm font-grotesk placeholder:text-body-grey';
const LABEL_CLS = 'block font-heading text-[8px] text-body-grey mb-1 uppercase';
const SAVE_CHIP_CLS = 'flex-none rounded-full bg-black/[.18] px-3.5 py-2 font-heading text-[8px] text-white disabled:opacity-50';

/**
 * Third of four owner-settings sub-screens (Task 12 of the ShopSettings
 * split). Sections below come from ShopSettings.tsx (COUNTER QR CODE,
 * SQUARE STAMP ITEMS, REDEMPTION SECURITY) per Nik's explicit ruling —
 * ShopSettings.tsx itself is untouched and stays routable until Task 14
 * removes it. The two old per-section save buttons (SAVE STAMP ITEMS, SAVE
 * REDEMPTION SETTINGS) are collapsed into one handleSaveAll, driven by the
 * header's SAVE chip (Task 15 removed the body's redundant full-width
 * duplicate — same handler, same disabled condition, so nothing was lost);
 * the QR section has nothing to save.
 *
 * The QR section renders the shared <ShopQRCode> component (already used on
 * ShopOwnerDashboard) instead of duplicating ShopSettings.tsx's older inline
 * QRCodeSVG block — same counter-QR feature, one implementation, plus
 * copy-link/print for free.
 */
function CounterSettings() {
    const navigate = useNavigate();
    const { userAddress } = useAuthStore();
    const { loading, shopId, shopName, shopRow } = useShopSettingsLoad();

    // Square stamp-eligible items (till payments). catalogItems === null means
    // "not loaded yet" (distinct from a genuinely empty Square Item Library,
    // which is catalogItems === []) — same distinction ShopSettings.tsx made.
    const [catalogItems, setCatalogItems] = useState<CatalogStampItem[] | null>(null);
    const [catalogError, setCatalogError] = useState<string | null>(null);
    const [eligibleIds, setEligibleIds] = useState<string[]>([]);   // variation ids
    const [savingStampItems, setSavingStampItems] = useState(false);
    // Distinguishes "never configured" (DB null) from "owner deliberately saved
    // empty" ([]) — both surface as eligibleIds === [] in state, so loadCatalog
    // needs this separate flag to know whether to pre-tick suggested items.
    const [hasSavedSelection, setHasSavedSelection] = useState(false);
    // Redemption security (054/task 4): anti-phishing secret word + reward
    // name. redemption_secret is an ungranted column — it can ONLY be read
    // via getMyShopPrivate(), never via getShopByOwner's public projection.
    const [redemptionSecret, setRedemptionSecret] = useState('');
    const [rewardLabel, setRewardLabel] = useState('');
    const [savingRedemption, setSavingRedemption] = useState(false);
    const [savingAll, setSavingAll] = useState(false);

    // Populate section state from the shared loader's row — mirrors the
    // relevant slice of ShopSettings.tsx's loadShopData().
    useEffect(() => {
        if (!shopRow) return;
        setHasSavedSelection(Array.isArray(shopRow.stamp_eligible_item_ids));
        if (Array.isArray(shopRow.stamp_eligible_item_ids)) setEligibleIds(shopRow.stamp_eligible_item_ids);
    }, [shopRow]);

    // Section-specific read the shared loader doesn't do (see brief) —
    // replicated here exactly as ShopSettings.tsx's loadShopData() does it:
    // fired once a DB row exists, not on the capability-only path.
    useEffect(() => {
        if (!shopRow) return;
        getMyShopPrivate().then((priv) => {
            setRedemptionSecret(priv?.redemption_secret ?? '');
            setRewardLabel(priv?.reward_label ?? '');
        });
    }, [shopRow]);

    async function loadCatalog() {
        setCatalogError(null);
        try {
            const items = await getSquareCatalogItems();
            setCatalogItems(items);
            // First-time default (spec): pre-tick beverage-ish items only when the
            // owner has never saved a selection — [] can also mean "deliberately
            // saved empty", which must NOT be clobbered with suggested defaults.
            if (!hasSavedSelection) {
                setEligibleIds(items.filter((i) => i.suggested).flatMap((i) => i.variationIds));
            }
        } catch {
            // A fetch/auth failure is NOT the same as a genuinely empty Square
            // Item Library — proven live 2026-07-12 when an empty catalog showed
            // "IS SQUARE CONNECTED?" to a connected shop.
            setCatalogItems(null);
            setCatalogError('COULDN\'T LOAD YOUR SQUARE MENU — TRY AGAIN');
        }
    }
    function toggleItem(item: CatalogStampItem, on: boolean) {
        setEligibleIds((prev) => on
            ? [...new Set([...prev, ...item.variationIds])]
            : prev.filter((v) => !item.variationIds.includes(v)));
    }
    async function saveStampItems() {
        if (!shopId) return;
        setSavingStampItems(true);
        try {
            const ok = await updateShopSettings({ shopId, stamp_eligible_item_ids: eligibleIds });
            if (!ok) throw new Error('Save failed');
            setHasSavedSelection(true);
        } catch (err) {
            console.error('Failed to save stamp items:', err);
            throw err;
        } finally {
            setSavingStampItems(false);
        }
    }

    async function handleSaveRedemption() {
        if (!shopId) return;
        setSavingRedemption(true);
        try {
            const result = await updateShopSettings({
                shopId,
                // Send null rather than '' when cleared — an empty string would
                // still render an (empty) secret block on the reward card,
                // which is worse than rendering none at all.
                redemption_secret: redemptionSecret.trim() || null,
                reward_label: rewardLabel.trim() || null,
            });
            if (!result) throw new Error('Save failed');
        } catch (err) {
            console.error('Failed to save redemption settings:', err);
            throw err;
        } finally {
            setSavingRedemption(false);
        }
    }

    // ONE combined save for the whole COUNTER group. The QR section has
    // nothing to save. Square items only saves once the catalog has actually
    // been loaded (catalogItems !== null) — sending eligibleIds unconditionally
    // would silently overwrite a never-touched selection with [] the first
    // time an owner saves ANY counter setting, the same class of bug the
    // Task 11 review caught for custom loot.
    async function handleSaveAll() {
        if (!shopId) return;
        setSavingAll(true);
        const failed: string[] = [];
        // Guard on a non-empty loaded catalog, not just "loaded" — the old
        // page's SAVE STAMP ITEMS button only ever existed in the
        // catalogItems.length > 0 branch (an empty library rendered a
        // message, no save path). A genuinely empty Square catalog (not a
        // failed load, which resets catalogItems to null) must not clobber a
        // previously saved selection with [] just because the owner hit
        // SAVE COUNTER SETTINGS for an unrelated reason, e.g. the secret word.
        if (catalogItems !== null && catalogItems.length > 0) {
            try { await saveStampItems(); } catch { failed.push('Square stamp items'); }
        }
        try { await handleSaveRedemption(); } catch { failed.push('redemption security'); }
        setSavingAll(false);
        if (failed.length > 0) {
            alert(`Some counter sections failed to save: ${failed.join(', ')}. Try again.`);
        } else {
            alert('Counter settings saved!');
        }
    }

    if (loading) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="Loading…">
                <div className="space-y-3.5">
                    <div className="bg-white rounded-[10px] h-24 animate-pulse" />
                    <div className="bg-white rounded-[10px] h-64 animate-pulse" />
                </div>
            </OwnerLoadingShell>
        );
    }

    if (!shopId) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="No shop yet">
                <div className="bg-white rounded-[10px] text-center py-12">
                    <Coffee size={40} className="mx-auto mb-4 text-coral" />
                    <p className="font-heading text-[10px] text-body-grey">NO SHOP REGISTERED YET</p>
                </div>
            </OwnerLoadingShell>
        );
    }

    // The owner is always the one viewing this screen — shopRow.owner_address
    // is the public projection's copy; userAddress covers the capability-only
    // path (no DB row yet) where shopRow is null.
    const ownerAddress = shopRow?.owner_address || userAddress || '';

    return (
        <OwnerShell
            active="setup"
            header={
                <CoralHeader
                    statusLabel="SETUP"
                    eyebrow="COUNTER SETTINGS"
                    title={shopName}
                    chip={
                        <button
                            onClick={handleSaveAll}
                            disabled={savingAll || savingStampItems || savingRedemption}
                            className={SAVE_CHIP_CLS}
                        >
                            {savingAll ? 'SAVING…' : 'SAVE'}
                        </button>
                    }
                />
            }
        >
        <div className="space-y-3.5 pb-8">
            {/* sr-only: CoralHeader's title already carries the shop name —
                mockup drops the repeated in-body page title. */}
            <h1 className="sr-only">Counter</h1>

            <button
                onClick={() => navigate('/shops/settings')}
                className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral py-2 flex items-center gap-2"
            >
                <ArrowLeft size={13} /> Settings
            </button>

            {/* Counter QR Code */}
            {ownerAddress && (
                <ShopQRCode shopOwnerAddress={ownerAddress} shopName={shopName} />
            )}

            {/* Secondary entry point only. A shop with no tag reaches this
                flow from SETUP; this is for one that has a tag and wants
                another for a second till. */}
            <button
                onClick={() => navigate('/shops/settings/tag')}
                className="font-grotesk font-bold text-[13px] text-coral py-2"
            >
                Order an NFC tag →
            </button>

            {/* Square stamp-eligible items — only meaningful when the till is connected */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Square stamp items</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    Till payments award one stamp per eligible item in the order (max 5).
                    Pick which menu items count — usually your coffees. Nothing selected = one stamp per payment.
                </p>
                {catalogItems === null ? (
                    <div>
                        <button
                            onClick={loadCatalog}
                            className="rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[12px] px-3 py-2 hover:border-coral hover:text-coral"
                        >
                            Load Square menu
                        </button>
                        {catalogError && (
                            <p className="font-grotesk font-medium text-[13px] text-error mt-3">{catalogError}</p>
                        )}
                    </div>
                ) : catalogItems.length === 0 ? (
                    <p className="font-grotesk font-medium text-[13px] text-body-grey">
                        Your Square item library is empty. Add menu items in the Square dashboard
                        (Items &gt; Item Library), then reload — ad-hoc amount sales can't earn
                        multi-stamps, so until then it's one stamp per payment.
                    </p>
                ) : (
                    <div className="max-h-64 overflow-y-auto space-y-1">
                        {catalogItems.map((item) => {
                            const noVariations = item.variationIds.length === 0;
                            return (
                                <label key={item.id} className={`flex items-center gap-3 py-2 font-grotesk font-medium text-sm text-primary ${noVariations ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        className="w-5 h-5 shrink-0 accent-coral"
                                        checked={!noVariations && item.variationIds.every((v) => eligibleIds.includes(v))}
                                        disabled={noVariations}
                                        onChange={(e) => toggleItem(item, e.target.checked)}
                                    />
                                    {item.name}
                                    {item.category && <span className="text-body-grey">({item.category})</span>}
                                    {item.suggested && <span className="text-coral text-[9px] font-heading">SUGGESTED</span>}
                                    {noVariations && <span className="text-body-grey text-[9px] font-heading">NO VARIATIONS</span>}
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Redemption security (054/task 4): secret word + reward name */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Redemption security</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    An anti-phishing phrase, like a bank's. Set both below to help
                    staff confirm a redemption at the counter.
                </p>

                <label htmlFor="redemption-secret" className={LABEL_CLS}>Secret word</label>
                <input
                    id="redemption-secret"
                    type="text"
                    maxLength={32}
                    value={redemptionSecret}
                    onChange={(e) => setRedemptionSecret(e.target.value)}
                    placeholder="PURPLE OTTER"
                    className={`${INPUT_CLS} mb-1`}
                />
                <p className="font-grotesk font-medium text-[12px] text-body-grey mt-1 mb-3.5">
                    Shown on the customer's reward screen. Teach it to your baristas —
                    if the screen doesn't show it, don't hand over the reward.
                </p>

                <label htmlFor="reward-label" className={LABEL_CLS}>Reward name</label>
                <input
                    id="reward-label"
                    type="text"
                    maxLength={32}
                    value={rewardLabel}
                    onChange={(e) => setRewardLabel(e.target.value)}
                    placeholder="FREE COFFEE"
                    className={`${INPUT_CLS} mb-1`}
                />
                <p className="font-grotesk font-medium text-[12px] text-body-grey mt-1">
                    What the customer is redeeming. Leave blank for "Free coffee".
                </p>
            </div>
        </div>
        </OwnerShell>
    );
}

export default CounterSettings;
